import React, { createContext, useContext, useEffect } from "react";
import {
  Easing,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import type {
  SharedValue,
  EasingFunction,
  EasingFunctionFactory,
} from "react-native-reanimated";

// Module-level constant for stable easing reference
const DEFAULT_EASING = Easing.bezier(0.25, 0.1, 0.25, 1);

interface MarqueeControllerContextValue {
  progress: SharedValue<number>; // 0-1 progress through the cycle
}

const MarqueeControllerContext = createContext<
  MarqueeControllerContextValue | undefined
>(undefined);

interface MarqueeControllerProps {
  children: React.ReactNode;
  cycleDuration?: number; // Animation duration in ms (excludes pauses)
  pauseDuration?: number; // Pause at each end in ms
  easing?: EasingFunction | EasingFunctionFactory; // Easing function for the animation
  resetKey?: string | number; // When this changes, animation restarts from beginning
}

export const MarqueeController = ({
  children,
  cycleDuration = 6000, // 6s of actual movement
  pauseDuration = 1000, // 1s pause at each end
  easing = DEFAULT_EASING, // Smooth easing similar to ease-in-out
  resetKey,
}: MarqueeControllerProps) => {
  // Shared progress value that goes 0 -> 1 -> 0 -> 1...
  const progress = useSharedValue(0);

  useEffect(() => {
    const halfDuration = cycleDuration / 2;

    // Reset to 0 and restart animation (triggered when resetKey changes)
    progress.value = 0;

    // Animate progress: 0 -> pause -> 1 -> pause -> 0 -> repeat
    progress.value = withRepeat(
      withSequence(
        // Pause at the start
        withDelay(pauseDuration, withTiming(0, { duration: 0 })),
        // Scroll out: 0 to 1
        withTiming(1, {
          duration: halfDuration,
          easing,
        }),
        // Pause at the end
        withDelay(pauseDuration, withTiming(1, { duration: 0 })),
        // Scroll back: 1 to 0
        withTiming(0, {
          duration: halfDuration,
          easing,
        }),
      ),
      -1, // infinite
      false,
    );
  }, [cycleDuration, pauseDuration, easing, progress, resetKey]);

  return (
    <MarqueeControllerContext.Provider value={{ progress }}>
      {children}
    </MarqueeControllerContext.Provider>
  );
};

export const useMarqueeController = () => {
  const context = useContext(MarqueeControllerContext);
  if (!context) {
    throw new Error(
      "useMarqueeController must be used within MarqueeController",
    );
  }
  return context;
};
